import { useRef } from "react";
import { useAppDispatch } from "store/hooks";
import { fromJS } from "immutable";
import {
  off,
  get,
  DatabaseReference,
  onChildChanged,
  onChildAdded,
  onChildRemoved,
  DataSnapshot,
} from "firebase/database";
import logger from "logging/logger";
import * as firebase from "../userStatus.firebase";
import actions from "../redux/actions";

const useUserStatusActions = () => {
  const dispatch = useAppDispatch();
  const dbRef = useRef<DatabaseReference>();

  const updateUserStatus = ({
    spaceId,
    userId,
    data,
  }: {
    spaceId: string | null;
    userId: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
  }) => {
    if (spaceId && userId) {
      firebase.updateUserStatus(spaceId, userId, data);
      dispatch(
        actions.updateUserStatus({
          ...data,
          userId,
        }),
      );
    }
  };

  const removeUserFromSpace = ({
    spaceId,
    roomId,
    userId,
  }: {
    spaceId: string | null;
    roomId: string;
    userId: string;
  }) => {
    if (spaceId) {
      firebase.removeUserFromSpace(spaceId, userId, roomId);
    }
  };

  const subscribe = ({ spaceId }: { spaceId: string | null }) => {
    if (!spaceId) {
      return;
    }

    dbRef.current = firebase.getSpaceUserStatusRef(spaceId);

    get(dbRef.current)
      .then((snapshot) => {
        const data = snapshot.val();
        // eslint-disable-next-line promise/always-return
        if (data) {
          dispatch(actions.syncUserStatus(fromJS(data)));
        }
      })
      .catch((error) => {
        logger.warn(
          "[removeUserFromSpace] Error fetching user status data:",
          error,
        );
      });

    const handler = (snapshot: DataSnapshot) => {
      const data = snapshot.val();

      if (data) {
        dispatch(actions.updateUserStatus(data));
      }
    };

    onChildChanged(dbRef.current, handler);
    onChildAdded(dbRef.current, handler);
    onChildRemoved(dbRef.current, (snapshot) => {
      const { key } = snapshot;
      if (key) {
        dispatch(actions.removeUserStatus(key));
      }
    });
  };

  const unsubscribe = () => {
    if (dbRef.current) {
      off(dbRef.current);
    }
  };

  return {
    removeUserFromSpace,
    updateUserStatus,
    subscribe,
    unsubscribe,
  };
};

export default useUserStatusActions;
